/**
 * Die Prüfpunkte der Platzbeurteilung nach DIN 18034.
 *
 * Einfriedung, angrenzende Bäume und Gebäude, Gewässer, Zuwegung: die
 * Punkte, die bei jeder Prüfung am Platz selbst abgegangen werden – nicht
 * an einem Gerät.
 *
 * Sie standen von Anfang an in einer Tabelle und nicht als Aufzählung im
 * Programm, genau damit der Prüfer Wortlaut und Reihenfolge selbst pflegen
 * kann. Gebaut wurde davon aber nur das Lesen. Bernd konnte die Liste
 * sehen und nicht anfassen.
 *
 * Stilllegen statt löschen: an einem Kriterium hängen die Befunde aller
 * bisherigen Prüfungen, und ein Bericht von vor zwei Jahren muss lesbar
 * bleiben. Ein stillgelegter Punkt fehlt in neuen Bögen und steht in den
 * alten weiter.
 */

import { useState } from 'react'
import {
  useAllePlatzkriterien,
  usePlatzkriteriumAendern,
  usePlatzkriteriumLoeschen,
  usePlatzkriteriumAnlegen,
} from '../api/abfragen'
import type { Platzkriterium } from '../api/typen'
import { Blatt } from '../bausteine/Blatt'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis } from '../bausteine/Hinweise'
import { darfVerwalten, useKonto } from '../konto/KontoKontext'

export function Pruefpunkte() {
  const { konto } = useKonto()
  const kriterien = useAllePlatzkriterien()
  const aendern = usePlatzkriteriumAendern()
  const [bearbeitet, setzeBearbeitet] = useState<Platzkriterium | 'neu' | null>(null)

  const darf = darfVerwalten(konto)

  if (kriterien.isLoading) return <Ladeanzeige />
  if (kriterien.error) return <Fehlerhinweis fehler={kriterien.error} />

  const liste = kriterien.data ?? []

  /* Reihenfolge ändern durch Neuvergabe aller Sortierungen, nicht durch
     Tausch zweier Werte: ein Tausch tut nichts, wenn zwei Punkte dieselbe
     Nummer tragen – und dann ist die Reihenfolge ohnehin willkürlich. */
  async function verschieben(index: number, richtung: -1 | 1) {
    const ziel = index + richtung
    if (ziel < 0 || ziel >= liste.length) return

    const neu = [...liste]
    const [bewegt] = neu.splice(index, 1)
    neu.splice(ziel, 0, bewegt)

    for (const [i, punkt] of neu.entries()) {
      const sortierung = (i + 1) * 10
      if (punkt.sortierung === sortierung) continue
      await aendern.mutateAsync({ id: punkt.id, sortierung })
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">Prüfpunkte Spielplatz</h1>
        {darf && (
          <button
            type="button"
            className="knopf-haupt ml-auto"
            onClick={() => setzeBearbeitet('neu')}
          >
            Neuer Prüfpunkt
          </button>
        )}
      </header>

      <p className="text-base text-slate-700">
        Diese Punkte stehen in jedem neuen Prüfbogen unter „Spielplatz nach
        DIN 18034" – vor den Geräten. Wortlaut und Reihenfolge bestimmen Sie.
      </p>

      <Fehlerhinweis fehler={aendern.error} />

      {liste.length === 0 ? (
        <Leerhinweis titel="Keine Prüfpunkte" />
      ) : (
        <ol className="space-y-2">
          {liste.map((k, i) => (
            <li
              key={k.id}
              className={`karte flex items-start gap-3 ${
                k.aktiv ? '' : 'border-dashed opacity-60'
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-semibold">
                  {k.bezeichnung}
                  {!k.aktiv && (
                    <span className="ml-2 font-normal text-slate-600">
                      · stillgelegt
                    </span>
                  )}
                </div>
                <div className="text-base text-slate-700">
                  {k.gruppe}
                  {k.normverweis ? ` · ${k.normverweis}` : ''}
                </div>
                {k.hinweis && (
                  <div className="mt-1 text-base text-slate-600">{k.hinweis}</div>
                )}
              </div>

              {darf && (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    className="knopf-still px-2 text-base"
                    aria-label={`„${k.bezeichnung}" nach oben`}
                    disabled={i === 0 || aendern.isPending}
                    onClick={() => void verschieben(i, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="knopf-still px-2 text-base"
                    aria-label={`„${k.bezeichnung}" nach unten`}
                    disabled={i === liste.length - 1 || aendern.isPending}
                    onClick={() => void verschieben(i, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="knopf-still px-3 text-base"
                    onClick={() => setzeBearbeitet(k)}
                  >
                    Ändern
                  </button>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {bearbeitet && (
        <PunktBlatt
          punkt={bearbeitet === 'neu' ? null : bearbeitet}
          anzahl={liste.length}
          schliessen={() => setzeBearbeitet(null)}
        />
      )}
    </div>
  )
}

function PunktBlatt({
  punkt,
  anzahl,
  schliessen,
}: {
  punkt: Platzkriterium | null
  anzahl: number
  schliessen: () => void
}) {
  const anlegen = usePlatzkriteriumAnlegen()
  const aendern = usePlatzkriteriumAendern()
  const loeschen = usePlatzkriteriumLoeschen()
  const laeuft = anlegen.isPending || aendern.isPending

  return (
    <Blatt
      titel={punkt ? 'Prüfpunkt ändern' : 'Neuer Prüfpunkt'}
      untertitel={punkt?.bezeichnung}
      schliessen={schliessen}
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="submit"
            form="punkt"
            className="knopf-haupt flex-1"
            disabled={laeuft}
          >
            {laeuft ? 'Speichert …' : 'Speichern'}
          </button>
        </div>
      }
    >
      <form
        id="punkt"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          const f = new FormData(e.currentTarget)
          const text = (name: string) => String(f.get(name) ?? '').trim()

          const werte = {
            gruppe: text('gruppe'),
            bezeichnung: text('bezeichnung'),
            hinweis: text('hinweis') || null,
            normverweis: text('normverweis') || null,
          }

          if (punkt) {
            aendern.mutate({ id: punkt.id, ...werte }, { onSuccess: schliessen })
          } else {
            anlegen.mutate(werte, { onSuccess: schliessen })
          }
        }}
      >
        <div>
          <label htmlFor="p-bezeichnung">Bezeichnung</label>
          <input
            id="p-bezeichnung"
            name="bezeichnung"
            required
            maxLength={200}
            defaultValue={punkt?.bezeichnung ?? ''}
          />
          <p className="mt-1 text-base text-slate-600">
            So steht der Punkt im Prüfbogen und im Bericht.
          </p>
        </div>

        <div>
          <label htmlFor="p-gruppe">Gruppe</label>
          <input
            id="p-gruppe"
            name="gruppe"
            required
            maxLength={60}
            list="p-gruppen"
            defaultValue={punkt?.gruppe ?? ''}
          />
          {/* Vorschläge statt Auswahlliste: die vorhandenen Gruppen sind der
              Regelfall, eine neue muss aber möglich bleiben. */}
          <datalist id="p-gruppen">
            <option value="Umfeld" />
            <option value="Grün" />
            <option value="Fläche" />
            <option value="Ausstattung" />
          </datalist>
        </div>

        <div>
          <label htmlFor="p-hinweis">Hinweis für den Prüfer</label>
          <textarea
            id="p-hinweis"
            name="hinweis"
            rows={2}
            defaultValue={punkt?.hinweis ?? ''}
          />
          <p className="mt-1 text-base text-slate-600">
            Steht im Bogen unter dem Punkt. Für das, woran man draußen denken soll.
          </p>
        </div>

        <div>
          <label htmlFor="p-norm">Normverweis</label>
          <input
            id="p-norm"
            name="normverweis"
            maxLength={60}
            placeholder="DIN 18034"
            defaultValue={punkt?.normverweis ?? ''}
          />
        </div>

        {punkt && (
          <div className="rounded-feld border-2 border-slate-200 p-3">
            <p className="font-semibold">
              {punkt.aktiv ? 'Punkt stilllegen' : 'Punkt wieder verwenden'}
            </p>
            <p className="mt-1 text-base text-slate-600">
              Ein stillgelegter Punkt erscheint in keinem neuen Bogen mehr. In
              bereits geschriebenen Berichten bleibt er stehen – gelöscht wird
              nichts.
            </p>
            <button
              type="button"
              className="knopf-still mt-2 px-3 text-base"
              onClick={() =>
                aendern.mutate(
                  { id: punkt.id, aktiv: !punkt.aktiv },
                  { onSuccess: schliessen },
                )
              }
            >
              {punkt.aktiv ? 'Stilllegen' : 'Wieder verwenden'}
            </button>
          </div>
        )}

        {/* Löschen für den Fall, der wirklich vorkommt: beim Einrichten hat
            sich jemand verschrieben und will die Zeile weghaben, nicht
            stillgelegt in der Liste stehen lassen.

            Sobald eine Prüfung den Punkt beurteilt hat, weist der Server es
            ab und nennt die Zahl der Prüfungen. Die Entscheidung fällt dort
            und nicht hier – die Oberfläche weiß nicht, was daran hängt. */}
        {punkt && (
          <div className="rounded-feld border-2 border-stufe-3 p-3">
            <p className="font-semibold text-stufe-3">Punkt löschen</p>
            <p className="mt-1 text-base text-slate-600">
              Nur solange keine Prüfung diesen Punkt beurteilt hat — für den
              Verschreiber beim Einrichten. Danach lässt er sich nur noch
              stilllegen, weil er sonst in bereits geschriebenen Berichten
              fehlen würde.
            </p>
            <Fehlerhinweis fehler={loeschen.error} />
            <button
              type="button"
              className="knopf-warnung mt-2 px-3 text-base"
              disabled={loeschen.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `„${punkt.bezeichnung}" endgültig löschen?\n\n` +
                      'Das lässt sich nicht rückgängig machen.',
                  )
                ) {
                  loeschen.mutate(punkt.id, { onSuccess: schliessen })
                }
              }}
            >
              {loeschen.isPending ? 'Löscht …' : 'Endgültig löschen'}
            </button>
          </div>
        )}

        {!punkt && anzahl > 0 && (
          <p className="text-base text-slate-600">
            Der neue Punkt kommt ans Ende der Liste. Verschieben lässt er sich
            danach mit den Pfeilen.
          </p>
        )}

        <Fehlerhinweis fehler={anlegen.error} />
        <Fehlerhinweis fehler={aendern.error} />
      </form>
    </Blatt>
  )
}
